import { useMemo } from 'react';

import { Color } from '@/constants/color';
import Badge from '@/ui/components/Badge/Badge';

import { ClassificationConfiguration, Props } from './types';

const Segments = {
  ACTIVE: 'active',
  DISCHARGED: 'discharged',
  ELIGIBLE: 'eligible',
  NOT_ELIGIBLE: 'not_eligible',
  NOT_TREATABLE: 'not_treatable',
  TREATABLE: 'treatable',
  LEAD: 'lead',
  WAITLISTED: 'waitlisted',
  UNKNOWN_TREATABILITY: 'unknown_treatability'
};

const Classifications = {
  AT_RISK: 'at_risk',
  COLD: 'cold',
  ENGAGED: 'engaged',
  THAWED: 'thawed'
};

// Deprecated segments
const EXCLUDED_SEGMENTS = ['eligible', 'not_eligible'];

// Unknown Treatability if none of the following are present
const TREATABILITY_SEGMENTS = ['treatable', 'not_treatable', 'waitlisted'];

// Classification configuration
const ClassificationConfig: { [key: string]: ClassificationConfiguration } = {
  [Segments.ACTIVE]: { color: Color.GREEN, order: 1 },
  [Segments.DISCHARGED]: { color: Color.RED, order: 3 },
  [Segments.TREATABLE]: { color: Color.BLUE, order: 2 },
  [Segments.NOT_TREATABLE]: { color: Color.GRAY, order: 2 },
  [Segments.LEAD]: { color: Color.YELLOW, order: 3 },
  [Segments.WAITLISTED]: { color: Color.AMBER, order: 1 },
  [Segments.UNKNOWN_TREATABILITY]: {
    color: Color.GRAY,
    order: 1
  },
  [Classifications.AT_RISK]: { color: Color.GRAY, order: 4 },
  [Classifications.COLD]: { color: Color.GRAY, order: 4 },
  [Classifications.ENGAGED]: { color: Color.GRAY, order: 4 },
  [Classifications.THAWED]: { color: Color.GRAY, order: 4 }
};

const ALLOWED_CLASSIFICATIONS = [
  Classifications.AT_RISK,
  Classifications.COLD,
  Classifications.ENGAGED,
  Classifications.THAWED
];

function ClassificationsList({ classifications }: Props) {
  const classificationFilter = (classification: string) => {
    return (
      (classification.startsWith('segment') &&
        !EXCLUDED_SEGMENTS.includes(`segment.${classification}`.toLowerCase())) ||
      ALLOWED_CLASSIFICATIONS.includes(classification)
    );
  };

  // Extract segments from classifications and sort them by preferred order
  const filteredClassifications = useMemo(() => {
    const list = classifications
      .filter(classificationFilter)
      .map((str) => {
        return str.replace('segment.', '');
      })
      .sort((segmentA, segmentB) => {
        const segmentAlookup = String(segmentA).toLowerCase();
        const segmentBlookup = String(segmentB).toLowerCase();
        const posA = ClassificationConfig[segmentAlookup]?.order || 0;
        const posB = ClassificationConfig[segmentBlookup]?.order || 0;

        if (posA < posB) {
          return -1;
        }
        if (posA > posB) {
          return 1;
        }
        return 0;
      });

    // HACK: If no treatability segment is present, add unknown treatability as a segment
    if (!list.some((el: string) => TREATABILITY_SEGMENTS.includes(el))) {
      list.push('unknown_treatability');
    }

    return list;
  }, [classifications]);

  const ClassificationItem = ({ segment }: { segment: string }) => {
    const segmentLookup = String(segment).toLowerCase();
    const config = ClassificationConfig[segmentLookup];

    return <Badge color={config?.color} text={segment.replace('_', ' ')} />;
  };

  return (
    <div className="flex gap-1">
      {filteredClassifications.map((classification) => (
        <ClassificationItem key={classification} segment={classification} />
      ))}
    </div>
  );
}

export default ClassificationsList;
